import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Modal } from './shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

export default function PaymentsTab({ paymentData, onRefresh, showMarkDeletion, setShowMarkDeletion, deletionForm, setDeletionForm }) {
  const [actionLoading, setActionLoading] = useState(null);

  const sendReminder = async (orgId) => {
    setActionLoading(`reminder-${orgId}`);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/system/payment/send-reminder`, 
        { org_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Purring sendt til ${response.data.org_name}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke sende purring');
    } finally {
      setActionLoading(null);
    }
  };

  const suspendFeatures = async (orgId) => {
    setActionLoading(`suspend-${orgId}`);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/system/payment/suspend-features`, 
        { org_id: orgId, send_email: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Funksjoner suspendert for ${response.data.org_name}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke suspendere');
    } finally {
      setActionLoading(null);
    }
  };

  const deactivateAccount = async (orgId) => {
    setActionLoading(`deactivate-${orgId}`);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/system/payment/deactivate-account`, 
        { org_id: orgId, send_email: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Konto deaktivert for ${response.data.org_name}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke deaktivere');
    } finally {
      setActionLoading(null);
    }
  };

  const markForDeletion = async (e) => {
    e.preventDefault();
    if (!showMarkDeletion) return;
    
    setActionLoading(`deletion-${showMarkDeletion.id}`);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/system/payment/mark-for-deletion`, 
        { 
          org_id: showMarkDeletion.id, 
          send_email: deletionForm.send_email,
          deletion_days: deletionForm.deletion_days,
          reason: deletionForm.reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.org_name} merket for sletting`);
      setShowMarkDeletion(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke merke for sletting');
    } finally {
      setActionLoading(null);
    }
  };

  const reactivate = async (orgId) => {
    setActionLoading(`reactivate-${orgId}`);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/system/payment/reactivate`, 
        { org_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.org_name} reaktivert`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke reaktivere');
    } finally {
      setActionLoading(null);
    }
  };

  const unmarkDeletion = async (orgId) => {
    setActionLoading(`unmark-${orgId}`);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/system/payment/unmark-deletion`, 
        { org_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Slettingsmerking fjernet');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke fjerne merking');
    } finally {
      setActionLoading(null);
    }
  };

  if (!paymentData) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  const { summary, overdue, suspended, deactivated, marked_for_deletion } = paymentData;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-400">{summary.overdue_count}</div>
          <div className="text-sm text-yellow-200">Forfalt betaling</div>
        </div>
        <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-400">{summary.suspended_count}</div>
          <div className="text-sm text-orange-200">Suspendert</div>
        </div>
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-400">{summary.deactivated_count}</div>
          <div className="text-sm text-red-200">Deaktivert</div>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-400">{summary.deletion_pending_count}</div>
          <div className="text-sm text-gray-400">Venter på sletting</div>
        </div>
      </div>

      {/* Overdue Payments */}
      {overdue.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-yellow-400">⚠️ Forfalt betaling ({overdue.length})</h2>
          <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
            {overdue.map(sub => (
              <div key={sub.organization_id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-white">{sub.org_name}</div>
                    <div className="text-sm text-gray-400">
                      Org.nr: {sub.org_number} • Mistet betalinger: {sub.missed_payments_count || 1}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(sub.missed_payments_count || 0) < 1 && (
                      <button
                        onClick={() => sendReminder(sub.organization_id)}
                        disabled={actionLoading === `reminder-${sub.organization_id}`}
                        className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                      >
                        {actionLoading === `reminder-${sub.organization_id}` ? '...' : '1. Purring'}
                      </button>
                    )}
                    {(sub.missed_payments_count || 0) >= 1 && (sub.missed_payments_count || 0) < 2 && (
                      <button
                        onClick={() => suspendFeatures(sub.organization_id)}
                        disabled={actionLoading === `suspend-${sub.organization_id}`}
                        className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50"
                      >
                        {actionLoading === `suspend-${sub.organization_id}` ? '...' : '2. Suspender'}
                      </button>
                    )}
                    {(sub.missed_payments_count || 0) >= 2 && (
                      <button
                        onClick={() => deactivateAccount(sub.organization_id)}
                        disabled={actionLoading === `deactivate-${sub.organization_id}`}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {actionLoading === `deactivate-${sub.organization_id}` ? '...' : '3. Deaktiver'}
                      </button>
                    )}
                    <button
                      onClick={() => reactivate(sub.organization_id)}
                      disabled={actionLoading === `reactivate-${sub.organization_id}`}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Reaktiver
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suspended Accounts */}
      {suspended.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-orange-400">🔒 Suspenderte kontoer ({suspended.length})</h2>
          <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
            {suspended.map(sub => (
              <div key={sub.organization_id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-white">{sub.org_name}</div>
                  <div className="text-sm text-gray-400">
                    Suspendert: {sub.features_suspended_at ? new Date(sub.features_suspended_at).toLocaleDateString('nb-NO') : '-'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => deactivateAccount(sub.organization_id)}
                    disabled={actionLoading === `deactivate-${sub.organization_id}`}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    Deaktiver
                  </button>
                  <button
                    onClick={() => reactivate(sub.organization_id)}
                    disabled={actionLoading === `reactivate-${sub.organization_id}`}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Reaktiver
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deactivated Accounts */}
      {deactivated.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-red-400">🚫 Deaktiverte kontoer ({deactivated.length})</h2>
          <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
            {deactivated.map(sub => (
              <div key={sub.organization_id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-white">{sub.org_name}</div>
                  <div className="text-sm text-gray-400">
                    Deaktivert: {sub.account_deactivated_at ? new Date(sub.account_deactivated_at).toLocaleDateString('nb-NO') : '-'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowMarkDeletion({ id: sub.organization_id, name: sub.org_name })}
                    className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-500"
                  >
                    Merk for sletting
                  </button>
                  <button
                    onClick={() => reactivate(sub.organization_id)}
                    disabled={actionLoading === `reactivate-${sub.organization_id}`}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Reaktiver
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Marked for Deletion */}
      {marked_for_deletion.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-400">🗑️ Merket for sletting ({marked_for_deletion.length})</h2>
          <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
            {marked_for_deletion.map(sub => (
              <div key={sub.organization_id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-white">{sub.org_name}</div>
                  <div className="text-sm text-gray-400">
                    Merket: {sub.marked_for_deletion_at ? new Date(sub.marked_for_deletion_at).toLocaleDateString('nb-NO') : '-'}
                  </div>
                </div>
                <button
                  onClick={() => unmarkDeletion(sub.organization_id)}
                  disabled={actionLoading === `unmark-${sub.organization_id}`}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Fjern merking
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {overdue.length === 0 && suspended.length === 0 && deactivated.length === 0 && marked_for_deletion.length === 0 && (
        <div className="bg-green-900/30 border border-green-700 rounded-lg p-8 text-center">
          <div className="text-green-400 text-lg font-medium">✓ Alle betalinger er i orden</div>
          <div className="text-green-200 text-sm mt-1">Ingen ubetalte fakturaer eller problemer</div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400">
        <p><strong>Reaksjonsmønster:</strong></p>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li><strong>1. purring:</strong> Betalingspåminnelse sendes pr. e-post</li>
          <li><strong>2. purring:</strong> Funksjoner deaktiveres, e-postvarsel sendes</li>
          <li><strong>3. purring:</strong> Konto deaktiveres fullstendig, e-postvarsel sendes</li>
          <li><strong>Sletting:</strong> Admin merker for sletting, velger om e-post skal sendes</li>
        </ol>
      </div>

      {/* Mark for Deletion Modal */}
      {showMarkDeletion && (
        <Modal title={`Merk ${showMarkDeletion.name} for sletting`} onClose={() => setShowMarkDeletion(null)}>
          <form onSubmit={markForDeletion} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Årsak</label>
              <select 
                value={deletionForm.reason} 
                onChange={e => setDeletionForm({...deletionForm, reason: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
              >
                <option value="manglende betaling">Manglende betaling</option>
                <option value="brukerforespørsel">Brukerforespørsel</option>
                <option value="inaktivitet">Inaktivitet</option>
                <option value="annet">Annet</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Dager før sletting</label>
              <input 
                type="number" 
                value={deletionForm.deletion_days} 
                onChange={e => setDeletionForm({...deletionForm, deletion_days: parseInt(e.target.value)})}
                min={1} max={90}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="send_deletion_email" 
                checked={deletionForm.send_email} 
                onChange={e => setDeletionForm({...deletionForm, send_email: e.target.checked})} 
                className="rounded"
              />
              <label htmlFor="send_deletion_email" className="text-sm">Send e-postvarsel om sletting</label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowMarkDeletion(null)} className="flex-1 py-2 border border-gray-600 rounded-lg hover:bg-gray-700">
                Avbryt
              </button>
              <button 
                type="submit" 
                disabled={actionLoading === `deletion-${showMarkDeletion.id}`}
                className="flex-1 py-2 bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === `deletion-${showMarkDeletion.id}` ? 'Behandler...' : 'Merk for sletting'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

